import { useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ArrowLeft, ShieldCheck } from "lucide-react";
import { startGoogleLogin, useAuth } from "@/auth/AuthProvider";

export default function Login() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && user) {
      navigate("/dashboard", { replace: true });
    }
  }, [user, loading, navigate]);

  return (
    <div
      className="min-h-screen bg-[#F4F3EE] text-[#111418] flex flex-col"
      data-testid="login-page"
    >
      {/* Slim top bar */}
      <header className="border-b border-[#D3D9DE]">
        <div className="gp-container h-16 flex items-center justify-between">
          <Link
            to="/"
            data-testid="login-back-link"
            className="inline-flex items-center gap-2 group"
          >
            <ArrowLeft
              size={14}
              className="text-[#2C333A] group-hover:text-[#D67229] transition-colors"
            />
            <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-[#2C333A] group-hover:text-[#D67229] transition-colors">
              Back to gushpay.com
            </span>
          </Link>
          <Link to="/" className="flex items-center gap-2" data-testid="login-logo">
            <span aria-hidden className="inline-block h-2 w-2 bg-[#D67229]" />
            <span className="font-display text-xl font-bold tracking-tight">
              GushPay
            </span>
          </Link>
        </div>
      </header>

      {/* Centered card */}
      <main className="flex-1 flex items-center justify-center px-6 py-16">
        <div className="w-full max-w-md">
          <div className="text-center">
            <span className="gp-overline">// Operator Sign-In</span>
            <h1
              data-testid="login-headline"
              className="mt-6 font-display text-4xl sm:text-5xl font-semibold tracking-tight leading-[1.05]"
            >
              Welcome to <span className="italic text-[#0F4C5C]">GushPay</span>
            </h1>
            <p
              data-testid="login-subheadline"
              className="mt-5 text-lg text-[#2C333A] leading-relaxed"
            >
              Oil &amp; Gas Bookkeeping Made Simple
            </p>
          </div>

          <div className="mt-12 relative">
            <div
              aria-hidden
              className="absolute -inset-3 border border-[#0F4C5C]/25"
            />
            <div className="relative bg-white border border-[#111418] p-8 md:p-10 shadow-[10px_10px_0_0_#0F4C5C]">
              <button
                type="button"
                onClick={startGoogleLogin}
                data-testid="login-google-button"
                className="w-full inline-flex items-center justify-center gap-3 h-12 bg-[#0F4C5C] hover:bg-[#D67229] text-white font-sans font-medium uppercase tracking-[0.14em] text-[0.78rem] transition-colors"
              >
                <GoogleMark />
                Continue with Google
              </button>

              <p className="mt-5 flex items-center justify-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em] text-[#2C333A]">
                <ShieldCheck size={12} className="text-[#0F4C5C]" />
                Secure authentication powered by Google OAuth
              </p>
            </div>
          </div>

          <p className="mt-10 text-center text-sm text-[#2C333A]">
            New to GushPay?{" "}
            <Link
              to="/pricing"
              data-testid="login-demo-link"
              className="text-[#D67229] hover:text-[#0F4C5C] transition-colors underline underline-offset-4"
            >
              See pricing &amp; start your free trial
            </Link>
            .
          </p>
        </div>
      </main>

      <footer className="border-t border-[#D3D9DE]">
        <div className="gp-container py-5 flex flex-col md:flex-row gap-2 items-center justify-between">
          <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-[#2C333A]">
            © {new Date().getFullYear()} GushPay Energy Software Co.
          </span>
          <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-[#8593A1]">
            Operator preview · v 1.0
          </span>
        </div>
      </footer>
    </div>
  );
}

function GoogleMark() {
  return (
    <svg
      aria-hidden
      viewBox="0 0 24 24"
      width="18"
      height="18"
      className="-ml-1"
    >
      <path
        fill="#FFFFFF"
        d="M21.6 12.227c0-.708-.064-1.388-.182-2.04H12v3.86h5.39a4.61 4.61 0 0 1-2.003 3.027v2.512h3.243c1.898-1.748 2.97-4.323 2.97-7.359Z"
      />
      <path
        fill="#FFFFFF"
        d="M12 22c2.7 0 4.966-.895 6.62-2.414l-3.243-2.512c-.9.605-2.052.96-3.377.96-2.596 0-4.793-1.752-5.578-4.106H3.067v2.585A9.997 9.997 0 0 0 12 22Z"
      />
      <path
        fill="#FFFFFF"
        d="M6.422 13.928a5.992 5.992 0 0 1 0-3.854V7.49H3.067a10.003 10.003 0 0 0 0 9.022l3.355-2.585Z"
      />
      <path
        fill="#FFFFFF"
        d="M12 5.96c1.469 0 2.787.505 3.826 1.498l2.87-2.87C16.96 2.99 14.694 2 12 2A9.997 9.997 0 0 0 3.067 7.49l3.355 2.585C7.207 7.713 9.404 5.96 12 5.96Z"
      />
    </svg>
  );
}
